import { useEffect, useState, type ReactNode } from "react";

import { api, useAlCambiar, useDatos } from "../api";
import { avisoEnvio, useAccion } from "../componentes/acciones";
import { Actividad } from "../componentes/Actividad";
import { Carga } from "../componentes/Carga";
import { nombrePortal, TarjetaDemo } from "../componentes/Demo";
import { DialogoDescarte, DialogoResultado, RESULTADOS } from "../componentes/Dialogos";
import { BadgeCanal, BadgeLead, BadgeMensaje } from "../componentes/Estados";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  ContactLink,
  EmptyState,
  Eyebrow,
  Heading,
  Icon,
  Input,
  Menu,
  Page,
  Progress,
  Select,
  Tabs,
  Text,
  Textarea,
  TopBar,
  type IconName,
} from "../ds";
import { ESTADOS_LEAD, fecha, fechaHora, hace, linkInstagram, linkWhatsapp, motivoDescarte, proveedor, rating, SENALES, sinProtocolo, telefono, usd } from "../formato";
import { usePlantillaElegida } from "../plantillaElegida";
import { Enlace } from "../router";
import { useSesion } from "../sesion";
import type { DetalleLead, EstadoLead, Resultado, ResultadoEnvio } from "../tipos";
import s from "./Detalle.module.css";

export function Detalle({ id }: { id: string }) {
  const { datos: d, error, recargar } = useDatos<DetalleLead>(`/api/leads/${id}`);
  const { sesion } = useSesion();
  const { pendiente, ejecutar } = useAccion();
  const { elegida } = usePlantillaElegida();
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [descartar, setDescartar] = useState(false);
  useAlCambiar(recargar);

  useEffect(() => {
    if (d) document.title = `${d.nombre} · Prospección Klavia`;
  }, [d]);

  if (!d) {
    return (
      <>
        <TopBar title="Inmobiliaria" />
        <Page width="none">
          {error?.includes("No existe") ? (
            <EmptyState icon="search" title="No existe esa inmobiliaria" action={<Button href="/leads" as={Enlace}>Volver a leads</Button>}>
              Puede que se haya borrado o que el link esté incompleto.
            </EmptyState>
          ) : (
            <Carga error={error} onReintentar={recargar} filas={8} />
          )}
        </Page>
      </>
    );
  }

  const borradorWa = d.mensajes.find((m) => m.canal === "whatsapp" && (m.estado === "borrador" || m.estado === "aprobado"));
  const chatwoot = sesion?.integraciones.chatwoot;
  const puedeEnviar = Boolean(chatwoot?.configurado && chatwoot.bandeja);
  const cerrada = d.estado === "baja" || d.estado === "descartada" || d.optOut;
  const conversacion = d.mensajes.find((m) => m.conversacion)?.conversacion;
  const urlConversacion = conversacion && chatwoot?.url && chatwoot.cuenta ? `${chatwoot.url}/app/accounts/${chatwoot.cuenta}/conversations/${conversacion}` : null;

  const enviar = () =>
    borradorWa &&
    ejecutar(
      "enviar",
      () => api<ResultadoEnvio>(`/api/mensajes/${borradorWa.id}/enviar`, { metodo: "POST", cuerpo: { plantilla: elegida?.nombre ?? null } }),
      avisoEnvio(d.nombre),
    );
  const prepararBorrador = () =>
    ejecutar(
      "borrador",
      () => api<{ afectadas: number }>("/api/leads/lote", { metodo: "POST", cuerpo: { ids: [d.id], accion: "borradores" } }),
      (r) => (r.afectadas ? "Borrador listo en la cola" : "No corresponde: ya tuvo un primer WhatsApp o no califica"),
    );
  const confirmarDescarte = (motivo: string) =>
    ejecutar("descartar", () => api(`/api/leads/${d.id}`, { metodo: "PATCH", cuerpo: { estado: "descartada", motivo } }), "Descartada").then((r) => {
      if (r !== undefined) setDescartar(false);
    });

  const lugar = [d.localidad, d.zona && d.zona !== d.localidad ? `zona ${d.zona}` : null].filter(Boolean).join(" · ");

  return (
    <>
      <TopBar
        title={d.nombre}
        subtitle={`${lugar ? `${lugar}. ` : ""}${d.fuentes.some((f) => f.fuente === "manual") ? "Agregada a mano" : "Encontrada"} el ${fecha(d.creadaEn)}.`}
        actions={
          <>
            <BadgeLead estado={d.estado} size="md" />
            <Menu
              label="Marcar resultado"
              align="right"
              trigger={
                <Button size="sm" variant="outline" iconAfter="chevron-down" disabled={d.estado === "baja"}>
                  Marcar resultado
                </Button>
              }
              items={RESULTADOS.map((r) => ({ label: r.etiqueta, dot: r.valor === "no_interesa" ? "danger" : "success", onSelect: () => setResultado(r.valor) }))}
            />
            {!cerrada ? (
              <Button size="sm" variant="danger-soft" icon="x" onClick={() => setDescartar(true)}>
                Descartar
              </Button>
            ) : null}
            {borradorWa ? (
              <Button
                size="sm"
                icon="send"
                pending={pendiente === "enviar" || borradorWa.estado === "aprobado"}
                disabled={!puedeEnviar}
                onClick={enviar}
                title={!puedeEnviar ? "Falta configurar Chatwoot" : elegida ? `Sale con la plantilla ${elegida.nombre} (se elige en la cola)` : undefined}
              >
                {sesion?.envio.modo === "plantilla" ? "Enviar plantilla" : "Enviar por Chatwoot"}
              </Button>
            ) : null}
          </>
        }
      />
      <Page width="none">
        <Breadcrumb linkAs={Enlace} items={[{ label: "Leads", href: "/leads" }, { label: d.nombre }]} />
        {d.optOut ? (
          <Alert tone="danger" title="No contactar">
            {d.optOutMotivo === "no interesa" ? "Dijo que no le interesa." : "Pidió no recibir más mensajes."} Queda fuera de todos los envíos, por cualquier canal.
          </Alert>
        ) : null}
        <div className={s.grilla}>
          <div className={s.columna}>
            <Contacto d={d} urlConversacion={urlConversacion} />
            <EstadoYNotas d={d} onGuardado={recargar} />
          </div>
          <div className={s.columna}>
            <Puntaje d={d} minimo={sesion?.umbrales.whatsapp ?? 60} />
            <TarjetaDemo d={d} onCambio={recargar} />
            <Fuentes d={d} />
          </div>
          <div className={s.columna}>
            <Card padding="md" className={s.historial}>
              <ProximoPaso
                d={d}
                hayBorrador={Boolean(borradorWa)}
                onPreparar={prepararBorrador}
                preparando={pendiente === "borrador"}
                onResultado={setResultado}
              />
              <Historial d={d} />
            </Card>
          </div>
        </div>
      </Page>
      <DialogoResultado inmobiliaria={resultado ? d : null} resultado={resultado} onCerrar={() => setResultado(null)} onListo={recargar} />
      <DialogoDescarte abierto={descartar} titulo="Descartar inmobiliaria" subtitulo={d.nombre} pendiente={pendiente === "descartar"} onCerrar={() => setDescartar(false)} onConfirmar={confirmarDescarte} />
    </>
  );
}

function Dato({ icono, etiqueta, children, nota }: { icono: IconName; etiqueta: string; children: ReactNode; nota?: ReactNode }) {
  return (
    <div className={s.dato}>
      <span className={s.datoIcono}>
        <Icon name={icono} size={16} />
      </span>
      <div className={s.datoTextos}>
        <Eyebrow>{etiqueta}</Eyebrow>
        <div className={s.datoValor}>{children}</div>
        {nota ? (
          <Text as="span" size="xs" tone="muted">
            {nota}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

function Contacto({ d, urlConversacion }: { d: DetalleLead; urlConversacion: string | null }) {
  const otrosEmails = (d.sitio?.emails ?? []).filter((e) => e !== d.email);
  const otrosTel = (d.sitio?.telefonos ?? []).filter((t) => t !== d.telefono && t !== d.whatsapp && `+549${t.slice(3)}` !== d.whatsapp);
  const sinDatos = !d.whatsapp && !d.telefono && !d.email && !d.instagram && !d.sitioWeb;
  return (
    <Card padding="md">
      <div className={s.cabecera}>
        <Heading level={2} size="sm">
          Contacto
        </Heading>
        {d.whatsapp ? (
          <Badge tone="success" size="sm" dot>
            WhatsApp móvil
          </Badge>
        ) : null}
      </div>
      <div className={s.datos}>
        {sinDatos ? <Text size="sm" tone="muted">No hay ningún dato de contacto todavía.</Text> : null}
        {d.whatsapp ? (
          <Dato icono="whatsapp" etiqueta="WhatsApp">
            <ContactLink href={linkWhatsapp(d.whatsapp)}>
              <span className={s.mono}>{telefono(d.whatsapp)}</span>
            </ContactLink>
          </Dato>
        ) : null}
        {d.telefono && `+549${d.telefono.slice(3)}` !== d.whatsapp ? (
          <Dato icono="phone" etiqueta="Teléfono">
            <ContactLink href={`tel:${d.telefono}`}>
              <span className={s.mono}>{telefono(d.telefono)}</span>
            </ContactLink>
          </Dato>
        ) : null}
        {otrosTel.length ? (
          <Dato icono="phone" etiqueta="Otros teléfonos" nota="Sacados del sitio web.">
            {otrosTel.map((t) => (
              <ContactLink key={t} href={`tel:${t}`}>
                <span className={s.mono}>{telefono(t)}</span>
              </ContactLink>
            ))}
          </Dato>
        ) : null}
        {d.email ? (
          <Dato icono="mail" etiqueta="Email" nota={otrosEmails.length ? `En el sitio también figura ${otrosEmails.join(", ")}.` : undefined}>
            <ContactLink href={`mailto:${d.email}`}>{d.email}</ContactLink>
          </Dato>
        ) : null}
        {d.direccion ? (
          <Dato icono="map-pin" etiqueta="Dirección">
            {d.direccion.replace(/, C?\d{4}[A-Z]{0,3} /, ", ").replace("Cdad. Autónoma de Buenos Aires", "CABA")}
          </Dato>
        ) : null}
        {d.sitioWeb ? (
          <Dato icono="globe" etiqueta="Sitio" nota={d.sitio?.proveedor ? `Hecho con ${proveedor(d.sitio.proveedor)}.` : undefined}>
            <ContactLink href={d.sitioWeb}>{sinProtocolo(d.sitioWeb)}</ContactLink>
          </Dato>
        ) : null}
        {d.instagram ? (
          <Dato icono="camera" etiqueta="Instagram">
            <ContactLink href={linkInstagram(d.instagram)}>@{d.instagram}</ContactLink>
          </Dato>
        ) : null}
      </div>
      <div className={s.botones}>
        {d.mapsUrl ? (
          <Button size="sm" variant="outline" icon="map" fullWidth href={d.mapsUrl} target="_blank">
            Google Maps
          </Button>
        ) : null}
        {d.sitioWeb ? (
          <Button size="sm" variant="outline" icon="globe" fullWidth href={d.sitioWeb} target="_blank">
            Sitio web
          </Button>
        ) : null}
        {d.instagram ? (
          <Button size="sm" variant="outline" icon="camera" fullWidth href={linkInstagram(d.instagram)} target="_blank">
            Instagram
          </Button>
        ) : null}
        {urlConversacion ? (
          <Button size="sm" variant="outline" icon="inbox" fullWidth href={urlConversacion} target="_blank">
            Chatwoot
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

function EstadoYNotas({ d, onGuardado }: { d: DetalleLead; onGuardado: () => void }) {
  const { pendiente, ejecutar } = useAccion();
  const [estado, setEstado] = useState<EstadoLead>(d.estado);
  const [motivo, setMotivo] = useState("");
  const [notas, setNotas] = useState(d.notas ?? "");
  const [cobrado, setCobrado] = useState(String(d.cobradoUsd));
  useEffect(() => {
    setEstado(d.estado);
    setNotas(d.notas ?? "");
    setCobrado(String(d.cobradoUsd));
    setMotivo("");
  }, [d.estado, d.notas, d.cobradoUsd]);

  const cambioEstado = estado !== d.estado;
  const cambioNotas = notas.trim() !== (d.notas ?? "");
  const cambioCobrado = cobrado.trim() !== String(d.cobradoUsd);
  const pideMotivo = cambioEstado && (estado === "descartada" || estado === "baja");
  const esCliente = estado === "cliente" || d.estado === "cliente" || d.cobradoUsd > 0;

  const guardar = async () => {
    const cuerpo: Record<string, unknown> = {};
    if (cambioEstado) cuerpo.estado = estado;
    if (pideMotivo) cuerpo.motivo = motivo.trim() || null;
    if (cambioNotas) cuerpo.notas = notas;
    if (cambioCobrado) cuerpo.cobradoUsd = cobrado.trim() || "0";
    const r = await ejecutar("guardar", () => api(`/api/leads/${d.id}`, { metodo: "PATCH", cuerpo }), "Cambios guardados");
    if (r !== undefined) onGuardado();
  };

  return (
    <Card padding="md">
      <div className={s.formulario}>
        <Heading level={2} size="sm">
          Estado y notas
        </Heading>
        <Select
          label="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoLead)}
          disabled={d.estado === "baja"}
          hint={d.estado === "baja" ? "Pidió no ser contactada: una baja no se revierte." : d.estado === "descartada" ? motivoDescarte(d.motivoDescarte) : undefined}
          options={ESTADOS_LEAD.filter((e) => e.valor !== "nueva" || d.estado === "nueva").map((e) => ({ value: e.valor, label: e.etiqueta }))}
        />
        {pideMotivo ? <Input label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={estado === "baja" ? "Pidió no recibir más mensajes" : "Por qué no es para Klavia"} /> : null}
        {esCliente ? (
          <Input
            label="Cash collected (US$)"
            value={cobrado}
            onChange={(e) => setCobrado(e.target.value)}
            inputMode="decimal"
            placeholder="0"
            hint={d.cobradoUsd ? `Hoy figura ${usd(d.cobradoUsd)}. Es el total cobrado, no un pago más.` : "El total que pagó hasta hoy. El tablero lo suma por plantilla de apertura."}
          />
        ) : null}
        <Textarea
          label="Notas"
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          rows={4}
          maxLength={4000}
          showCount
          placeholder="Con quién hablaste, qué le interesa, cuándo volver a escribirle."
        />
        <div>
          <Button size="sm" icon="check" pending={pendiente === "guardar"} disabled={!cambioEstado && !cambioNotas && !cambioCobrado} onClick={guardar}>
            Guardar cambios
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Puntaje({ d, minimo }: { d: DetalleLead; minimo: number }) {
  const senales = Object.entries(d.puntajeDetalle).sort((a, b) => b[1] - a[1]);
  return (
    <Card padding="md">
      <div className={s.cabecera}>
        <Heading level={2} size="sm">
          Puntaje
        </Heading>
        {d.puntuadaEn ? (
          <Text as="span" size="xs" tone="muted">
            Calculado el {fechaHora(d.puntuadaEn)}
          </Text>
        ) : null}
      </div>
      <div className={s.cifra}>
        <span className={s.grande}>{d.puntaje ?? "–"}</span>
        <Text as="span" size="md" tone="muted">
          de 100
        </Text>
      </div>
      <div className={s.barra}>
        <Progress value={d.puntaje ?? 0} label="Puntaje" />
        <span className={s.umbral} style={{ left: `${minimo}%` }} aria-hidden="true" />
      </div>
      <Text size="xs" tone="muted">
        La marca es el mínimo para la cola de WhatsApp: {minimo}.
      </Text>
      {senales.length ? (
        <ul className={s.senales}>
          {senales.map(([k, v]) => (
            <li key={k} className={s.senal}>
              <span className={s.senalIcono}>
                <Icon name={SENALES[k]?.icono ?? "info"} size={15} />
              </span>
              <Text as="span" size="sm" weight="semibold" tone="strong" className={s.senalTexto}>
                {SENALES[k]?.etiqueta ?? k}
                {k.startsWith("resenas") && d.resenas ? <Text as="span" size="xs" tone="muted"> · {d.resenas} reseñas</Text> : null}
                {k === "buen_rating" ? <Text as="span" size="xs" tone="muted"> · {rating(d.rating)} en Google</Text> : null}
                {k.startsWith("zona") && d.zona ? <Text as="span" size="xs" tone="muted"> · {d.zona}</Text> : null}
              </Text>
              <Text as="span" size="sm" mono weight="semibold" tone={v < 0 ? "danger" : "success"}>
                {v > 0 ? `+${v}` : v}
              </Text>
            </li>
          ))}
        </ul>
      ) : (
        <Text size="sm" tone="muted">
          {d.motivoDescarte ? motivoDescarte(d.motivoDescarte) : "Todavía no se puntuó."}
        </Text>
      )}
      {d.propiedadesActivas === null ? (
        <p className={s.nota}>Sin dato todavía: propiedades publicadas. Llega cuando se lean los portales.</p>
      ) : null}
    </Card>
  );
}

function Fuentes({ d }: { d: DetalleLead }) {
  const filas: { clave: string; icono: IconName; titulo: string; texto: string; href?: string }[] = [];
  for (const f of d.fuentes) {
    if (f.fuente === "google_places") {
      filas.push({ clave: String(f.id), icono: "map-pin", titulo: `Google Maps${f.zona ? ` · zona ${f.zona}` : ""}`, texto: `Primera vez el ${fechaHora(f.primeraVez)}.`, href: d.mapsUrl ?? undefined });
    } else if (f.fuente === "sitio_web") {
      const x = d.sitio;
      filas.push({
        clave: String(f.id),
        icono: "globe",
        titulo: `Sitio web${x?.proveedor ? ` · ${proveedor(x.proveedor)}` : ""}`,
        texto: x?.error
          ? `No se pudo leer (${hace(f.ultimaVez)}).`
          : `${x?.paginasLeidas === 1 ? "Una página leída" : `${x?.paginasLeidas ?? 0} páginas leídas`}: ${x?.emails.length ?? 0} emails, ${x?.telefonos.length ?? 0} teléfonos.`,
        href: f.externoId ?? undefined,
      });
    } else if (f.fuente === "manual") {
      filas.push({ clave: String(f.id), icono: "user-plus", titulo: "Carga manual", texto: `El ${fechaHora(f.primeraVez)}.` });
    } else {
      const datos = (f.datos ?? {}) as { origen?: string; motivo?: string };
      const texto =
        datos.origen?.startsWith("listado_")
          ? `Encontrado en los listados de ${nombrePortal(f.fuente)} ${datos.motivo === "telefono" ? "por su teléfono" : "por su nombre, en su zona"}.`
          : datos.origen === "panel"
            ? "Pegado a mano en el panel, para la demo."
            : "Link encontrado en su sitio.";
      filas.push({ clave: String(f.id), icono: "link", titulo: f.fuente.charAt(0).toUpperCase() + f.fuente.slice(1), texto, href: f.externoId ?? undefined });
    }
  }
  if (!d.fuentes.some((f) => ["zonaprop", "argenprop", "mercadolibre", "properati"].includes(f.fuente)) && d.sitio && !d.sitio.error) {
    filas.push({ clave: "portales", icono: "building-2", titulo: "Portales", texto: "El sitio no enlaza a Zonaprop, Argenprop ni MercadoLibre." });
  }
  return (
    <Card padding="md">
      <div className={s.cabecera}>
        <Heading level={2} size="sm">
          Fuentes
        </Heading>
        <Text as="span" size="xs" tone="muted">
          De dónde salió cada dato
        </Text>
      </div>
      <ul className={s.fuentes}>
        {filas.map((f) => (
          <li key={f.clave} className={s.fuente}>
            <span className={s.fuenteIcono}>
              <Icon name={f.icono} size={16} />
            </span>
            <div className={s.datoTextos}>
              <Text as="span" size="sm" weight="semibold" tone="strong">
                {f.titulo}
              </Text>
              <Text as="span" size="xs" tone="muted">
                {f.texto}
              </Text>
            </div>
            {f.href ? (
              <Button size="sm" variant="ghost" href={f.href} target="_blank" iconAfter="external-link">
                Ver
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ProximoPaso({
  d,
  hayBorrador,
  onPreparar,
  preparando,
  onResultado,
}: {
  d: DetalleLead;
  hayBorrador: boolean;
  onPreparar: () => void;
  preparando: boolean;
  onResultado: (r: Resultado) => void;
}) {
  const enviado = d.mensajes.filter((m) => m.estado === "enviado").sort((a, b) => (b.enviadoEn ?? "").localeCompare(a.enviadoEn ?? ""))[0];
  let texto: string;
  let accion: ReactNode = null;
  if (d.optOut || d.estado === "baja") texto = "Nada: no quiere que la contacten.";
  else if (d.estado === "descartada") texto = `Nada: está descartada. ${motivoDescarte(d.motivoDescarte)}.`;
  else if (d.estado === "cliente") texto = "Ya es cliente de Klavia.";
  else if (d.estado === "reunion") {
    texto = "Hay una reunión. Si contrata, marcala como cliente.";
    accion = (
      <Button size="sm" variant="secondary" icon="badge-check" onClick={() => onResultado("cliente")}>
        Es cliente
      </Button>
    );
  } else if (d.estado === "respondio") {
    // La fase del embudo vive en los eventos: la inmobiliaria sigue en `respondio`.
    const fase = (["pidio_mas", "seguimiento", "info_enviada"] as const).find((f) => d.eventos.some((e) => e.tipo === f)) ?? null;
    if (fase === "pidio_mas") {
      texto = "Le interesó la info. Coordiná una reunión o una demo.";
      accion = (
        <Button size="sm" variant="secondary" icon="calendar-check" onClick={() => onResultado("reunion")}>
          Reunión agendada
        </Button>
      );
    } else if (fase === "seguimiento" || fase === "info_enviada") {
      texto = fase === "seguimiento" ? "En seguimiento. Si pide más o quiere avanzar, marcalo." : "Ya tiene la info. Si pide más o quiere avanzar, marcalo.";
      accion = (
        <Button size="sm" variant="secondary" icon="sparkles" onClick={() => onResultado("pidio_mas")}>
          Le interesó la info
        </Button>
      );
    } else {
      texto = d.demo
        ? "Respondió. Si se mostró interesado, mandale su demo (el link está en «Demo») y marcalo."
        : "Respondió. Si se mostró interesado, mandale la info y marcalo.";
      accion = (
        <Button size="sm" variant="secondary" icon="send" onClick={() => onResultado("info_enviada")}>
          Se le mandó la info
        </Button>
      );
    }
  } else if (hayBorrador) {
    texto = "Mandar el primer WhatsApp. El borrador ya está en la cola.";
    accion = (
      <Button size="sm" variant="secondary" icon="whatsapp" as={Enlace} href="/cola">
        Ver en la cola
      </Button>
    );
  } else if (d.estado === "contactada") texto = `Esperar la respuesta. El último mensaje salió ${hace(enviado?.enviadoEn ?? null)}.`;
  else if (d.whatsapp && (d.puntaje ?? 0) > 0) {
    texto = "Preparar el primer WhatsApp.";
    accion = (
      <Button size="sm" variant="secondary" icon="plus" pending={preparando} onClick={onPreparar}>
        Preparar borrador
      </Button>
    );
  } else if (d.email) texto = "Sin WhatsApp móvil: queda para la secuencia de email.";
  else texto = "Sin WhatsApp ni email. Conseguí un contacto y cargalo en las notas.";

  return (
    <div className={s.proximo}>
      <Eyebrow tone="brand">Próximo paso</Eyebrow>
      <Text size="sm" weight="semibold" tone="strong">
        {texto}
      </Text>
      {accion ? <div>{accion}</div> : null}
    </div>
  );
}

function Historial({ d }: { d: DetalleLead }) {
  const [pestana, setPestana] = useState("actividad");
  return (
    <div className={s.historialCuerpo}>
      <Tabs
        value={pestana}
        onChange={setPestana}
        tabs={[
          { value: "actividad", label: "Actividad" },
          { value: "mensajes", label: "Mensajes", count: d.mensajes.length },
        ]}
      />
      {pestana === "actividad" ? (
        <Actividad detalle={d} />
      ) : d.mensajes.length === 0 ? (
        <EmptyState icon="inbox" title="Todavía no hay mensajes" tone="plain">
          Los borradores y envíos de email y WhatsApp aparecen acá.
        </EmptyState>
      ) : (
        <ul className={s.mensajes}>
          {d.mensajes.map((m) => (
            <li key={m.id} className={s.mensaje}>
              <div className={s.mensajeCabecera}>
                <BadgeCanal canal={m.canal} />
                <BadgeMensaje estado={m.estado} />
                {m.plantilla ? (
                  <Badge tone="neutral" size="sm" icon="file-text">
                    {m.plantilla}
                  </Badge>
                ) : null}
                <span className={s.espacio} />
                <Text as="span" size="xs" tone="muted">
                  {m.enviadoEn ? `Enviado el ${fechaHora(m.enviadoEn)}` : `Preparado el ${fechaHora(m.creadoEn)}`}
                </Text>
              </div>
              {m.asunto ? (
                <Text size="sm" weight="semibold" tone="strong">
                  {m.asunto}
                </Text>
              ) : null}
              <Text size="xs" className={s.mensajeTexto}>
                {m.cuerpo}
              </Text>
              {m.error && m.estado !== "cancelado" ? (
                <Alert tone="danger" compact>
                  {m.error}
                </Alert>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
